/**
 * Lógica compartida para datos del dashboard: claves de cache y construcción de
 * parámetros. Usado por el dashboard en vivo (App + ChartGrid) y por la
 * previsualización de reportes (ReportPreview) para no duplicar reglas.
 */

const DEFAULT_DYNAMIC_FILTER = {
  enabled: false,
  paramName: '',
  filterType: 'select',
  optionSource: 'query',
  optionsQuery: '',
  labelColumn: '',
  valueColumn: '',
  staticOptions: [],
  selectedValue: '',
  allowExecuteWithoutSelection: false,
};

export function getChartDynamicFilters(chart) {
  const cfg = chart?.advancedConfig;
  if (!cfg || typeof cfg !== 'object') return [];
  if (Array.isArray(cfg.dynamicFilters)) {
    return cfg.dynamicFilters
      .filter((f) => f && typeof f === 'object')
      .map((f) => ({ ...DEFAULT_DYNAMIC_FILTER, ...f }));
  }
  if (cfg.dynamicFilter && typeof cfg.dynamicFilter === 'object') {
    return [{ ...DEFAULT_DYNAMIC_FILTER, ...cfg.dynamicFilter }];
  }
  return [];
}

/** Ancho mínimo en columnas de la grilla (react-grid-layout, cols=12). */
export const GRID_DEFAULT_MIN_W = 3;
export const GRID_KPI_MIN_W = 2;
/** Por debajo de este ancho (px) los gráficos se apilan a ancho completo. */
export const DASHBOARD_STACK_BREAKPOINT = 900;
export const GRID_COLS = 12;

export function gridMinWForChartType(chartType) {
  return chartType === 'kpi' ? GRID_KPI_MIN_W : GRID_DEFAULT_MIN_W;
}

/** minH en react-grid-layout (edición / layout guardado). */
export function gridLayoutMinHForChartType(chartType) {
  return chartType === 'kpi' ? 2 : 3;
}

/** Altura mínima en filas de grilla según tipo (vista estrecha). */
export function gridMinHForChartType(chartType, savedH = 4) {
  if (chartType === 'kpi') return Math.max(savedH, gridLayoutMinHForChartType('kpi'));
  if (chartType === 'table') return Math.max(savedH, 5);
  if (chartType === 'map' || chartType === 'heatmapGrid' || chartType === 'imageDensity' || chartType === 'geoHeatmap') {
    return Math.max(savedH, 5);
  }
  return Math.max(savedH, 4);
}

/**
 * Apila todos los gráficos a ancho completo para móvil/tablet estrecho.
 * No modifica el layout guardado en BD; solo la vista.
 */
export function stackLayoutForNarrowViewport(layout, charts, { cols = GRID_COLS } = {}) {
  if (!Array.isArray(layout) || layout.length === 0) return layout || [];

  const chartById = new Map();
  (charts || []).forEach((chart) => {
    if (chart?.id != null) chartById.set(String(chart.id), chart);
  });

  const sorted = [...layout].sort((a, b) => a.y - b.y || a.x - b.x || String(a.i).localeCompare(String(b.i)));
  let currentY = 0;

  return sorted.map((item) => {
    const chart = chartById.get(String(item.i));
    const chartType = chart?.type;
    const h = gridMinHForChartType(chartType, item.h);
    const stacked = {
      ...item,
      i: chart?.id ?? item.i,
      x: 0,
      y: currentY,
      w: cols,
      h,
      minW: cols,
      maxW: cols,
    };
    currentY += h;
    return stacked;
  });
}

export function shouldStackDashboardLayout(containerWidth) {
  return containerWidth > 0 && containerWidth < DASHBOARD_STACK_BREAKPOINT;
}

export function defaultGridLayoutItem(chartId, chartType, { x = 0, y = 0, w = 4, h = 4 } = {}) {
  return {
    i: chartId,
    x,
    y,
    w,
    h,
    minW: gridMinWForChartType(chartType),
    maxW: 12,
    minH: chartType === 'kpi' ? 2 : 3,
  };
}

export function withGridLayoutConstraints(layoutItem, chartType) {
  if (!layoutItem || typeof layoutItem !== 'object') return layoutItem;
  const next = { ...layoutItem, minW: gridMinWForChartType(chartType) };
  if (chartType === 'kpi') {
    next.minH = gridLayoutMinHForChartType('kpi');
  }
  return next;
}

/**
 * Clave estable para cache de datos por (queryId, params). Misma fórmula que el dashboard.
 * @param {string|number} queryId
 * @param {object} params
 * @returns {string|null}
 */
export function getQueryDataKey(queryId, params) {
  if (queryId == null) return null;
  const p = params && typeof params === 'object' && !Array.isArray(params) ? params : {};
  const keys = Object.keys(p).filter(
    (k) => p[k] !== undefined && p[k] !== null && k !== '__proto__' && k !== 'constructor'
  );
  if (keys.length === 0) return String(queryId);
  keys.sort();
  const paramStr = keys.map((k) => `${k}=${String(p[k])}`).join('&');
  return `${queryId}|${paramStr}`;
}

/** Clave de cache incluyendo zona horaria del dashboard (afecta bucketing SQL). */
export function getDashboardQueryDataKey(queryId, params, timezone) {
  const tz =
    timezone != null && String(timezone).trim() !== '' ? String(timezone).trim() : 'UTC';
  return getQueryDataKey(queryId, { ...(params || {}), __dashboard_tz: tz });
}

/**
 * Parámetros para ejecutar la query de un gráfico: filtros globales + aliases + filtro dinámico del gráfico.
 * Misma lógica que ChartGrid para que la previsualización use exactamente los mismos parámetros.
 *
 * @param {object} chart - Config del gráfico (advancedConfig.dynamicFilter, etc.)
 * @param {object} options
 * @param {object} options.globalFilterValues
 * @param {object} options.globalFilterParamAliases
 * @param {string[]} options.globalFilterParamNames - Para saber si el filtro dinámico es global (no se añade como param del gráfico)
 * @param {object} [options.overrides] - Parámetros que sobrescriben (ej. valor recién elegido en dropdown)
 * @param {function} [options.getDynamicFilterValue] - (chart) => value para el filtro dinámico de ese gráfico
 * @returns {object} Parámetros para la query
 */
export function buildParamsForChart(chart, options = {}) {
  const {
    globalFilterValues = {},
    globalFilterParamAliases = {},
    globalFilterParamNames = [],
    overrides = {},
    getDynamicFilterValue,
  } = options;

  const params = { ...(globalFilterValues || {}), ...overrides };
  const aliases =
    globalFilterParamAliases && typeof globalFilterParamAliases === 'object'
      ? globalFilterParamAliases
      : {};

  const isParamGlobal = (paramName) =>
    (globalFilterParamNames || []).includes(String(paramName).trim());
  const filters = getChartDynamicFilters(chart);
  filters.forEach((df) => {
    if (!df?.enabled || !df?.paramName) return;
    const name = df.paramName;
    const allowEmpty = df.allowExecuteWithoutSelection === true;

    if (isParamGlobal(name)) {
      // Parámetro promovido a global: si permite vacío, asegurar que viaje '' para el NULLIF del SQL.
      if (allowEmpty && (params[name] === undefined || params[name] === null)) {
        params[name] = '';
      }
      return;
    }

    if (typeof getDynamicFilterValue !== 'function') return;
    const v =
      overrides[name] ??
      getDynamicFilterValue(chart, name) ??
      df.selectedValue;
    if (v !== undefined && v !== null && (allowEmpty || String(v).trim() !== '')) {
      const value = allowEmpty && String(v).trim() === '' ? '' : v;
      params[name] = df.filterType === 'date' && typeof value === 'string' ? value.slice(0, 10) : value;
    } else if (allowEmpty && !(name in params)) {
      // SQL con NULLIF(@p,'') necesita el parámetro presente aunque esté vacío.
      params[name] = '';
    }
  });

  Object.keys(params).forEach((paramName) => {
    const list = aliases[paramName];
    if (Array.isArray(list) && list.length > 0) {
      const val = params[paramName];
      list.forEach((alias) => {
        if (alias && String(alias).trim()) params[String(alias).trim()] = val;
      });
    }
  });

  return params;
}
